import { useCallback, useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import UValuePDFDocument from "./UValuePDFDocument";

const buttonClass = "pd-btn pd-btn--ghost";
const fileName = "pebbledash-uvalue-report.pdf";

const serialize = (value) => {
  if (value === undefined) return null;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (err) {
    console.warn("[u-values] Failed to serialise data for PDF export", err);
    return null;
  }
};

export default function DownloadUValueReportBtn({
  project,
  result,
  componentType,
  target,
  compliant,
  airLayer,
  outerLayers,
  innerLayers,
  disabled,
  dirty,
}) {
  const serializedProject = useMemo(() => serialize(project), [project]);
  const serializedResult = useMemo(() => serialize(result), [result]);
  const serializedOuter = useMemo(() => serialize(outerLayers), [outerLayers]);
  const serializedInner = useMemo(() => serialize(innerLayers), [innerLayers]);
  const serializedAir = useMemo(() => serialize(airLayer), [airLayer]);

  const [error, setError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const hasResult = useMemo(
    () => serializedResult && typeof serializedResult === "object" && Number.isFinite(serializedResult.uValue),
    [serializedResult],
  );

  const hasLayers = useMemo(
    () =>
      Array.isArray(serializedOuter) &&
      Array.isArray(serializedInner) &&
      serializedOuter.length > 0 &&
      serializedInner.length > 0,
    [serializedInner, serializedOuter],
  );

  const exportReady = useMemo(
    () => Boolean(componentType && serializedResult && serializedAir && hasLayers && hasResult),
    [componentType, hasLayers, hasResult, serializedAir, serializedResult],
  );

  useEffect(() => {
    if (exportReady) setError("");
  }, [exportReady]);

  const handleDownload = useCallback(async () => {
    if (!exportReady || disabled || isGenerating || dirty) return;

    setIsGenerating(true);
    setError("");

    try {
      const blob = await pdf(
        <UValuePDFDocument
          project={serializedProject}
          result={serializedResult}
          componentType={componentType}
          target={target}
          compliant={compliant}
          airLayer={serializedAir}
          outerLayers={serializedOuter}
          innerLayers={serializedInner}
        />,
        {},
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[u-values] PDF export failed", err);
      setError(err?.message || "Export unavailable");
    } finally {
      setIsGenerating(false);
    }
  }, [
    componentType,
    compliant,
    dirty,
    disabled,
    exportReady,
    isGenerating,
    serializedAir,
    serializedInner,
    serializedOuter,
    serializedProject,
    serializedResult,
    target,
  ]);

  const disabledState = disabled || isGenerating || !exportReady || dirty;
  const title =
    error || (dirty ? "Re-run the calculator to refresh the export." : !exportReady ? "Run the calculator to export." : "");

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={handleDownload}
      disabled={disabledState}
      aria-disabled={disabledState}
      title={title || undefined}
    >
      {isGenerating ? "Generating…" : "Export report"}
    </button>
  );
}
